import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

export interface FilterPillsProps {
  label: string;
  value: string;
  options: string[];
  onChange: (next: string) => void;
}

/** Pill-with-dropdown filter — design's FilterPills component (readers.jsx
 *  line 257). Replaces shadcn Select for the timezone / category filters so
 *  the visual matches the rest of the marketplace. Active when value !== 'all'. */
export const FilterPills: React.FC<FilterPillsProps> = ({ label, value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const active = value !== 'all';

  return (
    <div ref={wrapRef} className="relative flex-shrink-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 transition-colors"
        style={{
          padding: '8px 14px',
          borderRadius: 999,
          background: active ? 'var(--hj-surface-3)' : 'transparent',
          border: `1px solid ${active ? 'var(--hj-ink-4)' : 'var(--hj-hairline)'}`,
          color: 'var(--hj-ink-2)',
          fontSize: 13,
          fontFamily: 'inherit',
          cursor: 'pointer',
          minHeight: 44,
        }}
      >
        <span style={{ color: 'var(--hj-ink-3)' }}>{label}:</span>
        <span style={{ color: 'var(--hj-ink)', marginLeft: 2, fontWeight: 500 }}>
          {value === 'all' ? 'All' : value}
        </span>
        <ChevronDown size={12} strokeWidth={2} style={{ opacity: 0.7 }} />
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            background: 'var(--hj-surface-2)',
            border: '1px solid var(--hj-hairline)',
            borderRadius: 'var(--hj-r-sm)',
            padding: 6,
            zIndex: 10,
            minWidth: 200,
            maxHeight: 280,
            overflowY: 'auto',
            boxShadow: '0 8px 24px oklch(0 0 0 / 0.4)',
          }}
        >
          {options.map((o) => (
            <button
              key={o}
              type="button"
              onClick={() => {
                onChange(o);
                setOpen(false);
              }}
              className="block w-full text-left transition-colors"
              style={{
                padding: '8px 12px',
                background: o === value ? 'var(--hj-surface-3)' : 'transparent',
                border: 0,
                borderRadius: 6,
                color: o === value ? 'var(--hj-ink)' : 'var(--hj-ink-2)',
                fontSize: 13,
                fontFamily: 'inherit',
                cursor: 'pointer',
              }}
            >
              {o === 'all' ? `All ${label.toLowerCase()}s` : o}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
